import { BusinessTitlesUtil } from '@axiom/ui';
import {
  AvailabilityStatusKeys,
  CandidatesConst,
  CountryCodesConst,
  TaxonomyConst,
} from '@axiom/const';
import type {
  AfcSearchFilters,
  TalentSearchDisplayFilters,
  TalentSearchItem,
  TalentSearchItems,
  BarredLocation,
  Taxonomy,
  UserContact,
  TalentSearchDisplayFiltersCategory,
} from '@axiom/validation';

import type { TalentSearchActiveQueryFilters } from '../schema/talent-search-active-query-filters';

const { AvailabilityStatuses } = CandidatesConst;

const alwaysShowKeys = {
  PRACTICE_AREA: 'PRACTICE_AREA',
  FOCUS_AREA: 'FOCUS_AREA',
  INDUSTRIES: 'INDUSTRIES',
  COUNTRIES: 'COUNTRIES',
  STATES: 'STATES',
  BUSINESS_TITLES: 'BUSINESS_TITLES',
  BARRED_LOCATIONS: 'BARRED_LOCATIONS',
};

const alwaysShowKeysAlphabetical = new Set([
  alwaysShowKeys.INDUSTRIES,
  alwaysShowKeys.PRACTICE_AREA,
  alwaysShowKeys.FOCUS_AREA,
  alwaysShowKeys.STATES,
  alwaysShowKeys.BUSINESS_TITLES,
]);

/**
 * The orders number in the object below are
 * dictated by Product
 * */
const alwaysShowData = {
  [alwaysShowKeys.PRACTICE_AREA]: {},
  [alwaysShowKeys.FOCUS_AREA]: {},
  [alwaysShowKeys.INDUSTRIES]: {},
  [alwaysShowKeys.COUNTRIES]: {
    'United States': 0,
    'United Kingdom': 1,
    Canada: 2,
    Singapore: 3,
    'Hong Kong': 4,
    Germany: 5,
    Switzerland: 6,
  },
  [alwaysShowKeys.STATES]: {},
  [alwaysShowKeys.BUSINESS_TITLES]: {},
  [alwaysShowKeys.BARRED_LOCATIONS]: {},
};

export const WHITELISTED_COUNTRY_CODES = new Set([
  'US',
  'HK',
  'CA',
  'SG',
  'UK',
  'DE',
  'CH',
  'AU',
]);

export interface OrganizeDataProps {
  allFilters: AfcSearchFilters;
  defaultSort: TalentSearchDisplayFilters['sort'];
  activeFilters: TalentSearchActiveQueryFilters;
}

const barredLocationsGroupings = (
  allBL: AfcSearchFilters['barredLocations'],
  barredLocationsFilters: string[]
) => {
  const categoryLimits: Record<string, number> = {
    US: 5,
    Canada: 5,
    EU: 5,
    APAC: 3,
  };

  const alwaysShow = [] as TalentSearchItems['alwaysShow'];
  const showHide = [] as TalentSearchItems['showHide'];

  const categorizeLocation = (
    location: AfcSearchFilters['barredLocations'][number],
    categoryKey: keyof typeof categoryLimits
  ) => {
    if (categoryLimits[categoryKey] !== undefined) {
      const isChecked = barredLocationsFilters.includes(location.id);
      const targetArray =
        categoryLimits[categoryKey] > 0 ? alwaysShow : showHide;

      targetArray.push({ ...location, checked: isChecked });
      return categoryLimits[categoryKey]--;
    }
    return null;
  };

  allBL.forEach(barredLocation => {
    if (
      barredLocation.name.includes('Asia') ||
      barredLocation.name.includes('AUS') ||
      barredLocation.name.includes('NZ')
    ) {
      categorizeLocation(barredLocation, 'APAC');
    } else if (barredLocation.name.includes('US')) {
      categorizeLocation(barredLocation, 'US');
    } else if (barredLocation.name.includes('Canada')) {
      categorizeLocation(barredLocation, 'Canada');
    } else if (
      barredLocation.name.includes('EU') ||
      barredLocation.name.includes('UK')
    ) {
      categorizeLocation(barredLocation, 'EU');
    } else {
      const isChecked = barredLocationsFilters.includes(barredLocation.id);
      showHide.push({ ...barredLocation, checked: isChecked });
    }
  });
  return { alwaysShow, showHide };
};

const getBarredLocations = ({
  selectedBarredLocations,
  barredLocations,
}: {
  selectedBarredLocations: TalentSearchActiveQueryFilters;
  barredLocations: BarredLocation[];
}) => {
  if (!Array.isArray(selectedBarredLocations)) {
    return;
  }
  const locationNames = selectedBarredLocations
    .map(location => {
      return barredLocations.find(bl => bl.id === location)?.name;
    })
    .filter(Boolean);

  return locationNames.length > 0 ? locationNames : undefined;
};

const getPracticeAreas = ({
  selectedPracticeAreas,
  taxonomy,
}: {
  selectedPracticeAreas: TalentSearchActiveQueryFilters;
  taxonomy: Taxonomy;
}) => {
  if (!Array.isArray(selectedPracticeAreas)) {
    return;
  }
  const areaNames = selectedPracticeAreas
    .map(area => taxonomy.practiceAreas[area]?.fullName)
    .filter(Boolean);

  return areaNames.length > 0 ? areaNames : undefined;
};

const getIndustries = ({
  selectedIndustries,
  taxonomy,
}: {
  selectedIndustries: TalentSearchActiveQueryFilters;
  taxonomy: Taxonomy;
}) => {
  if (!Array.isArray(selectedIndustries)) {
    return;
  }
  const industryNames = selectedIndustries
    .map(industry => taxonomy.industries[industry]?.displayValue)
    .filter(Boolean);

  return industryNames.length > 0 ? industryNames : undefined;
};

const getCountriesFromCountryCodes = ({
  countryCodes,
}: {
  countryCodes: TalentSearchActiveQueryFilters;
}) => {
  if (!Array.isArray(countryCodes)) {
    return;
  }
  const countries = countryCodes
    .map(
      countryCode =>
        CountryCodesConst.CountryCodes[
          countryCode as keyof typeof CountryCodesConst.CountryCodes
        ]
    )
    .filter(Boolean);

  return countries.length > 0 ? countries : undefined;
};

const getYearsOfExperience = ({
  yearsOfExperience,
}: {
  yearsOfExperience: TalentSearchActiveQueryFilters;
}) => {
  const result = (
    Array.isArray(yearsOfExperience) ? yearsOfExperience : []
  ).map(item => JSON.stringify(item));
  return result.length > 0 ? result : undefined;
};

export const TalentSearchFilterUtil = {
  getAccountInformation: ({
    accounts = [],
  }: {
    accounts?: UserContact['accounts'];
  }) => {
    let accountInformation;
    if (!accounts || accounts.length === 0) {
      accountInformation = 'N/A';
    } else if (accounts.length === 1) {
      accountInformation = accounts[0]?.name;
    } else if (accounts.length > 1) {
      accountInformation = null;
    }
    return accountInformation;
  },

  getCandidateStatsFilters: (activeFilters: TalentSearchActiveQueryFilters) => {
    const possibleMarketingUTMs = new Set([
      'gclid',
      'hsa_acchsa_ad',
      'hsa_cam',
      'hsa_grp',
      'hsa_kw',
      'hsa_mt',
      'hsa_net',
      'hsa_src',
      'hsa_tgt',
      'hsa_ver',
      'utm_campaign',
      'utm_medium',
      'utm_source',
      'utm_term',
    ]);
    const newUsedFilters =
      Object.entries(activeFilters).length > 0
        ? Object.entries(activeFilters).reduce<TalentSearchActiveQueryFilters>(
            (acc, [key, value]) => {
              if (!possibleMarketingUTMs.has(key)) {
                acc[key] = value;
              }
              return acc;
            },
            {}
          )
        : {};
    const hasFilters = Object.entries(newUsedFilters).some(
      ([key, value]) =>
        !!key && !!value && key !== 'view' && key !== 'search' && key !== 'sort'
    );
    return {
      hasFilters,
      newUsedFilters,
    };
  },
  categorizeBarredLocationsItems: (
    data: TalentSearchDisplayFiltersCategory
  ) => {
    const categories = {
      US: {
        name: 'United States',
        id: 'US',
        item: {
          alwaysShow: [] as TalentSearchItems['alwaysShow'],
          showHide: [] as TalentSearchItems['showHide'],
        },
      },
      Canada: {
        name: 'Canada',
        id: 'Canada',
        item: {
          alwaysShow: [] as TalentSearchItems['alwaysShow'],
          showHide: [] as TalentSearchItems['showHide'],
        },
      },
      EU: {
        name: 'Europe',
        id: 'EU',
        item: {
          alwaysShow: [] as TalentSearchItems['alwaysShow'],
          showHide: [] as TalentSearchItems['showHide'],
        },
      },
      APAC: {
        name: 'APAC',
        id: 'Asia',
        item: {
          alwaysShow: [] as TalentSearchItems['alwaysShow'],
          showHide: [] as TalentSearchItems['showHide'],
        },
      },
      other: {
        name: 'Other',
        id: 'other',
        item: {
          alwaysShow: [] as TalentSearchItems['alwaysShow'],
          showHide: [] as TalentSearchItems['showHide'],
        },
      },
    };

    const categorizeItem = (
      item: TalentSearchItem,
      index: number,
      target: 'alwaysShow' | 'showHide'
    ) => {
      const itemWithIndex = { ...item, originalIndex: index };
      if (
        itemWithIndex.name.includes('Asia') ||
        itemWithIndex.name.includes('AUS') ||
        itemWithIndex.name.includes('NZ')
      ) {
        categories.APAC.item[target].push(itemWithIndex);
      } else if (itemWithIndex.name.includes('US')) {
        categories.US.item[target].push(itemWithIndex);
      } else if (itemWithIndex.name.includes('Canada')) {
        categories.Canada.item[target].push(itemWithIndex);
      } else if (
        itemWithIndex.name.includes('EU') ||
        itemWithIndex.name.includes('UK')
      ) {
        categories.EU.item[target].push(itemWithIndex);
      } else {
        categories.other.item[target].push(itemWithIndex);
      }
    };

    data.items.alwaysShow.forEach((item: TalentSearchItem, index: number) => {
      categorizeItem(item, index, 'alwaysShow');
    });

    data.items.showHide.forEach((item: TalentSearchItem, index: number) => {
      categorizeItem(item, index, 'showHide');
    });

    return categories;
  },
  getFilterData: ({
    activeFilters,
    barredLocations,
    taxonomy,
  }: {
    activeFilters: TalentSearchActiveQueryFilters;
    barredLocations: BarredLocation[];
    taxonomy: Taxonomy;
  }) => {
    const data = {
      barredLocations: getBarredLocations({
        selectedBarredLocations:
          activeFilters.barredLocations as TalentSearchActiveQueryFilters,
        barredLocations,
      }),
      practiceAreas: getPracticeAreas({
        selectedPracticeAreas:
          activeFilters.practiceAreas as TalentSearchActiveQueryFilters,
        taxonomy,
      }),
      industries: getIndustries({
        selectedIndustries:
          activeFilters.industries as TalentSearchActiveQueryFilters,
        taxonomy,
      }),
      yearsOfExperience: getYearsOfExperience({
        yearsOfExperience:
          activeFilters.yearsOfExperience as TalentSearchActiveQueryFilters,
      }),
      countries: getCountriesFromCountryCodes({
        countryCodes:
          activeFilters.addressCountryCode as TalentSearchActiveQueryFilters,
      }),
      state: activeFilters.state as Array<string | null | undefined>,
      businessTitles: activeFilters.businessTitles as Array<
        string | null | undefined
      >,
    };
    const isEmpty = Object.values(data).every(x => x === undefined);

    return isEmpty ? null : data;
  },
  organizeData: ({
    allFilters,
    defaultSort,
    activeFilters,
  }: OrganizeDataProps) => {
    const search = activeFilters.search ?? '';
    const sort = activeFilters.sort ?? '';

    const practiceAreas = Array.isArray(activeFilters.practiceAreas)
      ? activeFilters.practiceAreas
      : [];

    const focusAreas = Array.isArray(activeFilters.focusAreas)
      ? activeFilters.focusAreas
      : [];

    const industries = Array.isArray(activeFilters.industries)
      ? activeFilters.industries
      : [];

    const yearsOfExperience = Array.isArray(activeFilters.yearsOfExperience)
      ? activeFilters.yearsOfExperience
      : [];

    const addressCountryCode = Array.isArray(activeFilters.addressCountryCode)
      ? activeFilters.addressCountryCode
      : [];

    const state = Array.isArray(activeFilters.state) ? activeFilters.state : [];

    const availabilityStatus = Array.isArray(activeFilters.availabilityStatus)
      ? activeFilters.availabilityStatus
      : [];

    const barredLocations = Array.isArray(activeFilters.barredLocations)
      ? activeFilters.barredLocations
      : [];

    const businessTitles = Array.isArray(activeFilters.businessTitles)
      ? activeFilters.businessTitles
      : [];

    const reducerFn = (
      obj: TalentSearchItems,
      data: AfcSearchFilters[keyof AfcSearchFilters][number],
      checkData: TalentSearchActiveQueryFilters[keyof TalentSearchActiveQueryFilters],
      alwaysShowKey: keyof typeof alwaysShowKeys
    ) => {
      if (typeof data === 'object') {
        const item = {
          ...data,
          name:
            (data.id === TaxonomyConst.LawyerBusinessTitle
              ? BusinessTitlesUtil.locationConversion({
                  businessTitle: data.name,
                })
              : data.name) ?? '',
          checked: (Array.isArray(checkData) ? checkData : []).includes(
            data.id
          ),
        };

        // @ts-expect-error Skipping for now
        const alwaysShowIndex = alwaysShowData[alwaysShowKey][item.name];
        /**
         * Do to the set ordering in the alwaysShowData; every item has a set array index,
         * the array may end up with Y populated indices but have X length.
         * Example: [, 'cat', 'dog', , , 'fish'] >> length of 6
         * There is logic below that cleans this up.
         * */
        if (alwaysShowIndex !== undefined) {
          obj.alwaysShow[alwaysShowIndex] = item;
        } else if (
          alwaysShowKeysAlphabetical.has(alwaysShowKey) &&
          obj.alwaysShow.length < 5
        ) {
          obj.alwaysShow.push(item);
        } else {
          obj.showHide.push(item);
        }

        return obj;
      }
      return {
        alwaysShow: [],
        showHide: [],
      };
    };

    const getItems = (
      data: AfcSearchFilters[keyof AfcSearchFilters],
      fullDataSet: TalentSearchActiveQueryFilters[keyof TalentSearchActiveQueryFilters],
      alwaysShowKey: keyof typeof alwaysShowKeys
    ) => {
      const items = data.reduce(
        (obj, item) => reducerFn(obj, item, fullDataSet, alwaysShowKey),
        {
          alwaysShow: [] as TalentSearchItems['alwaysShow'],
          showHide: [] as TalentSearchItems['showHide'],
        }
      );

      /**
       * Removing NULL values from configured filters,
       * do to the value not being returned from the API
       * Example: [, 'cat', 'dog', , , 'fish'] >> length of 6
       * If the API does not return the index 3 item
       * we end up with a NULL in the array. The empty
       * indices translate to NULL.
       *
       * The below fixing down stream issues.
       * */

      items.alwaysShow = items.alwaysShow.filter(Boolean);

      return items;
    };

    const data = {
      search,
      sort: sort || defaultSort,
      categories: [],
      hasSearched: !!search,
    } as TalentSearchDisplayFilters;

    if (allFilters.primaryPracticeAreas.length > 0) {
      data.categories.push({
        name: 'practiceAreas',
        title: 'Practice Area',
        isOpen: true,
        selectedCount: practiceAreas.length || 0,
        items: getItems(
          allFilters.primaryPracticeAreas.sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
          practiceAreas,
          alwaysShowKeys.PRACTICE_AREA as keyof typeof alwaysShowKeys
        ),
      });
    }

    if (allFilters.primaryFocusAreas.length > 0) {
      data.categories.push({
        name: 'focusAreas',
        title: 'Focus Area',
        isOpen: true,
        selectedCount: focusAreas.length || 0,
        items: getItems(
          allFilters.primaryFocusAreas.sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
          focusAreas,
          alwaysShowKeys.FOCUS_AREA as keyof typeof alwaysShowKeys
        ),
      });
    }

    if (allFilters.industries.length > 0) {
      data.categories.push({
        name: 'industries',
        title: 'Industry',
        isOpen: true,
        selectedCount: industries.length || 0,
        items: getItems(
          allFilters.industries.sort((a, b) => a.name.localeCompare(b.name)),
          industries,
          alwaysShowKeys.INDUSTRIES as keyof typeof alwaysShowKeys
        ),
      });
    }

    if (allFilters.yearsOfExperience.length > 0) {
      data.categories.push({
        name: 'yearsOfExperience',
        title: 'Years Of Experience',
        isOpen: false,
        selectedCount: yearsOfExperience.length || 0,
        items: {
          alwaysShow: allFilters.yearsOfExperience.map(yoe => {
            const found = yearsOfExperience.find(yearOfExp => {
              return typeof yearOfExp === 'object' && typeof yoe.id === 'object'
                ? Number.parseInt(yearOfExp.start as string, 10) ===
                    yoe.id.start
                : null;
            });

            return {
              ...yoe,
              name: `${yoe.name} yrs`,
              checked: found !== undefined,
            };
          }),
          showHide: [],
        },
      });
    }
    if (allFilters.addressCountryCode.length > 0) {
      data.categories.push({
        name: 'addressCountryCode',
        title: 'Country',
        isOpen: false,
        selectedCount: addressCountryCode.length || 0,
        items: getItems(
          allFilters.addressCountryCode,
          addressCountryCode,
          alwaysShowKeys.COUNTRIES as keyof typeof alwaysShowKeys
        ),
      });
    }

    if (allFilters.state.length > 0) {
      data.categories.push({
        name: 'state',
        title: 'State',
        isOpen: false,
        selectedCount: state.length || 0,
        items: getItems(
          allFilters.state.sort((a, b) => a.name.localeCompare(b.name)),
          state,
          alwaysShowKeys.STATES as keyof typeof alwaysShowKeys
        ),
      });
    }
    if (allFilters.businessTitles.length > 0) {
      data.categories.push({
        name: 'businessTitles',
        title: 'Legal Role',
        isOpen: false,
        selectedCount: businessTitles.length || 0,
        items: getItems(
          allFilters.businessTitles,
          businessTitles,
          alwaysShowKeys.BUSINESS_TITLES as keyof typeof alwaysShowKeys
        ),
      });
    }

    data.categories.push({
      name: 'availabilityStatus',
      title: 'Availability',
      isOpen: false,
      selectedCount: availabilityStatus.length || 0,
      items: {
        alwaysShow: [
          {
            id: AvailabilityStatusKeys.AvailableNow,
            name: AvailabilityStatuses.AvailableNow,
            checked: availabilityStatus.includes(
              AvailabilityStatusKeys.AvailableNow
            ),
          },
          {
            id: AvailabilityStatusKeys.AvailableSoon,
            name: AvailabilityStatuses.AvailableSoon,
            checked: availabilityStatus.includes(
              AvailabilityStatusKeys.AvailableSoon
            ),
          },
          {
            id: AvailabilityStatusKeys.NotAvailable,
            name: AvailabilityStatuses.NotAvailable,
            checked: availabilityStatus.includes(
              AvailabilityStatusKeys.NotAvailable
            ),
          },
        ],
        showHide: [],
      },
    });
    if (allFilters.barredLocations.length > 0) {
      data.categories.push({
        name: 'barredLocations',
        title: 'Admission',
        isOpen: false,
        selectedCount: barredLocations.length || 0,
        items: barredLocationsGroupings(
          allFilters.barredLocations.sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
          barredLocations as string[]
        ),
      });
    }

    return data;
  },
  getSelectedFilters: (formData: TalentSearchDisplayFilters) => {
    const reducer = (data: TalentSearchItems) =>
      Object.values(data).reduce(
        (ary: Array<TalentSearchItem['id']>, items) => {
          items.forEach(item => {
            if (item.checked) ary.push(item.id);
          });

          return ary;
        },
        []
      );

    const data = formData.categories.reduce<TalentSearchActiveQueryFilters>(
      (obj, d) => {
        obj[d.name] = reducer(d.items) as string[];

        if (obj[d.name]?.length === 0) {
          obj[d.name] = undefined;
        }

        return obj;
      },
      {}
    );

    if (formData.search) {
      data.search = formData.search;
    }
    if (formData.sort && formData.sort !== 'relevance') {
      data.sort = formData.sort;
    }
    const totalNumberOfQueries = Object.values(data).reduce(
      (sum, val) => sum + (Array.isArray(val) ? val.length : 0),
      0
    );
    return {
      data,
      isUnderQueryLimit: totalNumberOfQueries < 21,
    };
  },
};
